import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  CircularProgress,
  Card,
  Divider,
  useTheme,
  IconButton,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { _detail_customer_for_admin_api } from "../../DAL/adminAuth/auth";
import { enqueueSnackbar } from "notistack";
import { makeStyles } from "@mui/styles";
import { show_proper_words } from "../../utils/constant";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import moment from "moment";
import {
  Assignment, // Case Type
  CalendarToday, // Occurrence Period
  Person, // Full Name
  Email, // Email
  Phone, // Phone
  LocationOn, // State
  CheckCircle, // Status
  DateRange, // Created At
} from "@mui/icons-material";

const useStyles = makeStyles((theme) => ({
  loading: {
    marginLeft: "50%",
    marginTop: "20%",
  },
  cardContainer: {
    maxWidth: 800,
    margin: "0 auto",
    borderRadius: "30px",
  },
  sectionTitle: {
    marginBottom: "20px",
    fontWeight: "bold !important",
  },
  propertyBox: {
    padding: "18px",
    textAlign: "center",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    width: "220px",
    overflow: "hidden",
  },
  divider: {
    // margin: theme.spacing(3, 0),
  },
  propertyItem: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    textAlign: "center",
  },
}));

const PropertyItem = ({ label, value, icon }) => {
  const classes = useStyles();
  return (
    <Box display="flex" alignItems="center" className={classes.propertyItem}>
      <Box sx={{ fontSize: "14px" }} mr={1}>
        {icon}
      </Box>
      <Box display="flex" flexDirection="column" sx={{ width: "100%" }}>
        <Typography variant="subtitle2" color="textSecondary" sx={{ fontSize: "12px" }}>
          {label}
        </Typography>
        <Typography 
          variant="h6" 
          fontWeight={600} 
          sx={{ 
            fontSize: "13px",
            wordBreak: "break-word",
            overflow: "hidden",
            textOverflow: "ellipsis"
          }}
        >
          {value}
        </Typography>
      </Box>
    </Box>
  );
};

export default function PropertyDetail() {
  const [isLoading, setIsLoading] = useState(true);
  const [propertyData, setPropertyData] = useState(null);
  const classes = useStyles();
  const theme = useTheme();
  const { id } = useParams();
  const navigate = useNavigate();

  const getPropertyDetail = async () => {
    setIsLoading(true);
    try {
      const result = await _detail_customer_for_admin_api(id);
      if (result.code === 200) {
        setPropertyData(result.case);
      } else {
        enqueueSnackbar(result.message || "Error loading property", {
          variant: "error",
        });
      }
    } catch (error) {
      enqueueSnackbar("Failed to fetch property details", {
        variant: "error",
      });
    }
    setIsLoading(false);
  };

  useEffect(() => {
    if (id) {
      getPropertyDetail();
    }
  }, [id]);

  if (isLoading) {
    return <CircularProgress className={classes.loading} color="primary" />;
  }

  if (!propertyData) {
    return (
      <Typography variant="h6" color="error" align="center" mt={5}>
        No property data found.
      </Typography>
    );
  }

  return (
    <>
      <div className="d-flex align-items-center">
        <IconButton
          className="back-screen-button "
          onClick={() => navigate(-1)}
        >
          <ArrowBackIcon />
        </IconButton>
        <div className="">
          <Typography
            variant="h5"
            className={classes.sectionTitle}
            sx={{ marginBottom: "0px !important" }}
          >
            Case Detail{" "}
          </Typography>
        </div>
      </div>

      {/* Top Row - 3 boxes */}
      <Card className="mui-card" elevation={3}>
        <Grid container className="mui-grid">
          <Grid item xs={12} sm={4} className="mui-grid-inside">
            <Box className={classes.propertyBox}>
              <PropertyItem
                label="Full Name"
                value={show_proper_words(propertyData?.user_id?.name)}
                icon={
                  <Person
                    fontSize="medium"
                    color="primary"
                    className="mui-icon"
                  />
                }
              />
            </Box>
          </Grid>
          <Grid item xs={12} sm={4} className="mui-grid-inside">
            <Box className={classes.propertyBox}>
              <PropertyItem
                label="Email"
                value={propertyData?.user_id?.email}
                icon={
                  <Email
                    fontSize="medium"
                    color="primary"
                    className="mui-icon"
                  />
                }
              />
            </Box>
          </Grid>
          <Grid item xs={12} sm={4} className="mui-grid-inside">
            <Box className={classes.propertyBox}>
              <PropertyItem
                label="Phone"
                value={propertyData?.phone}
                icon={
                  <Phone
                    fontSize="medium"
                    color="primary"
                    className="mui-icon"
                  />
                }
              />
            </Box>
          </Grid>
          <Grid
            item
            xs={12}
            sm={6}
            className="mui-grid-inside"
            sx={{ textAlign: "center", borderRight: "none !important" }}
          >
            <Box className={classes.propertyBox} sx={{ textAlign: "center" }}>
              <PropertyItem
                label="State"
                value={propertyData?.state}
                icon={
                  <LocationOn
                    fontSize="medium"
                    color="primary"
                    className="mui-icon"
                  />
                }
              />
            </Box>
          </Grid>
        </Grid>

        <Grid
          container
          className="mui-grid"
          sx={{ borderTop: "none", borderBottom: "none" }}
        >
          <Grid item xs={12} sm={4} className="mui-grid-inside">
            <Box className={classes.propertyBox}>
              <PropertyItem
                label="Case Type"
                value={show_proper_words(propertyData?.case_type)}
                icon={
                  <Assignment
                    fontSize="medium"
                    color="primary"
                    className="mui-icon"
                  />
                }
              />
            </Box>
          </Grid>
          <Grid item xs={12} sm={4} className="mui-grid-inside">
            <Box className={classes.propertyBox}>
              <PropertyItem
                label="Occurrence Period"
                value={show_proper_words(propertyData?.occurrencePeriod)}
                icon={
                  <CalendarToday
                    fontSize="medium"
                    color="primary"
                    className="mui-icon"
                  />
                }
              />
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} className="mui-grid-inside">
            <Box className={classes.propertyBox}>
              <PropertyItem
                label="Status"
                value={show_proper_words(propertyData?.status)}
                icon={
                  <CheckCircle
                    fontSize="medium"
                    color="primary"
                    className="mui-icon"
                  />
                }
              />
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box className={classes.propertyBox}>
              <PropertyItem
                label="Created At"
                value={show_proper_words(
                  moment(propertyData?.createdAt).format("YYYY-MM-DD")
                )}
                icon={
                  <DateRange
                    fontSize="medium"
                    color="primary"
                    className="mui-icon"
                  />
                }
              />
            </Box>
          </Grid>
        </Grid>
      </Card>
      <div className="col-12 mt-3">
        <Typography
          variant="h5"
          className={classes.sectionTitle}
          sx={{ marginBottom: "5px !important" }}
        >
          {"Description"}
        </Typography>

        <p>{propertyData?.caseDetails}</p>
      </div>
    </>
  );
}
